import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Camera, Upload, Loader2, ExternalLink, Crown, Sparkles, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import Seo from "@/components/Seo";

const MAX_SIZE = 6 * 1024 * 1024;

async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const out = String(reader.result || "");
      const comma = out.indexOf(",");
      resolve(comma >= 0 ? out.slice(comma + 1) : out);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function Scan() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/(jpeg|jpg|png|webp)/i.test(f.type)) {
      toast.error("JPG, PNG ou WEBP uniquement");
      return;
    }
    if (f.size > MAX_SIZE) {
      toast.error("Image trop volumineuse (max 6 MB)");
      return;
    }
    setPreview(URL.createObjectURL(f));
    setResult(null);
    setBusy(true);
    try {
      const b64 = await fileToBase64(f);
      const { data } = await api.post("/cards/scan", { image_base64: b64, mime_type: f.type });
      setResult(data);
      toast.success(t("scan.identified", "Carte identifiée"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!user || !user.email) {
    return (
      <div className="page-enter max-w-[700px] mx-auto px-6 py-20 text-center">
        <Camera className="w-16 h-16 mx-auto text-[#E11D48] mb-6" />
        <h1 className="font-heading font-extrabold text-4xl tracking-tight text-slate-900 mb-3">{t("scan.signin_title", "Connectez-vous pour scanner")}</h1>
        <p className="text-slate-500 mb-6">{t("scan.signin_desc", "Identifiez n'importe quelle carte par photo grâce à l'IA.")}</p>
        <Link to="/login" className="btn-primary">{t("nav.login")}</Link>
      </div>
    );
  }

  return (
    <div className="page-enter max-w-[1200px] mx-auto px-6 lg:px-10 py-12">
      <Seo
        title="AI card scanner"
        description="Identify any Pokémon card from a photo. Vision AI extracts name, set and number then returns the price page."
      />

      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="mb-10 flex items-center justify-between flex-wrap gap-3"
      >
        <div>
          <div className="text-xs font-semibold text-[#2563EB] uppercase tracking-wider mb-2 inline-flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> AI Scanner
          </div>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl tracking-tight text-slate-900">
            Card Scanner
          </h1>
          <p className="text-slate-500 mt-2 max-w-xl">
            {t("scan.subtitle", "Drop a clear photo of your card. Our vision AI extracts the name, set and number.")}
          </p>
        </div>
        <div className="pill pill-blue">
          {user.is_premium ? <Crown className="w-3.5 h-3.5" /> : null}
          {user.is_premium ? "Premium · unlimited" : "Free · 5 scans"}
        </div>
      </motion.div>

      <div className="grid lg:grid-cols-[1.1fr_1fr] gap-8">
        {/* Upload panel */}
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="flat-card p-6"
        >
          <div className="aspect-[3/4] border-2 border-dashed border-slate-200 bg-slate-50 rounded-2xl flex items-center justify-center overflow-hidden mb-4">
            {preview ? (
              <img src={preview} alt="preview" className="w-full h-full object-contain" data-testid="scan-preview" />
            ) : (
              <div className="text-center px-6">
                <ImageIcon className="w-16 h-16 mx-auto text-slate-300 mb-4" strokeWidth={1.5} />
                <div className="text-sm text-slate-500 font-medium">{t("scan.upload_hint", "Drop a photo or click to upload")}</div>
                <div className="text-xs text-slate-400 mt-1">JPG, PNG or WEBP · max 6 MB</div>
              </div>
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={onPick}
            className="hidden"
            data-testid="scan-file-input"
          />
          <div className="flex gap-2">
            <button
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              data-testid="scan-upload-btn"
              className="btn-primary flex-1 inline-flex items-center justify-center gap-2"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {busy ? t("scan.analyzing", "Analyzing…") : t("scan.upload", "Choose a photo")}
            </button>
            {preview && !busy && (
              <button onClick={() => { setPreview(null); setResult(null); }} className="btn-ghost">
                Reset
              </button>
            )}
          </div>
          {result?.identified && (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-5 pt-5 border-t border-slate-100 grid grid-cols-2 gap-3"
            >
              <div className="col-span-2 text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Identified</div>
              <div>
                <div className="text-xs text-slate-500">Name</div>
                <div className="font-semibold text-slate-900">{result.identified.name || "—"}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Set</div>
                <div className="text-slate-900 truncate">{result.identified.set_name || "—"}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500">N°</div>
                <div className="text-slate-900">{result.identified.number || "—"}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Confidence</div>
                <div className="text-[#E11D48] font-bold">{typeof result.identified.confidence === "number" ? `${(result.identified.confidence * 100).toFixed(0)}%` : "—"}</div>
              </div>
            </motion.div>
          )}
        </motion.div>

        {/* Candidates */}
        <motion.div
          initial={{ opacity: 0, x: 8 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Matches</div>
          <AnimatePresence mode="wait">
            {!result && !busy && (
              <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flat-card p-12 text-center">
                <Camera className="w-12 h-12 mx-auto mb-4 text-slate-300" strokeWidth={1.5} />
                <div className="text-sm text-slate-500">{t("scan.no_results", "Upload a card to see results.")}</div>
              </motion.div>
            )}
            {busy && (
              <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flat-card p-12 text-center">
                <Loader2 className="w-10 h-10 mx-auto mb-4 text-[#2563EB] animate-spin" />
                <div className="text-sm text-slate-600 animate-pulse">Querying vision AI…</div>
              </motion.div>
            )}
            {result && (
              <motion.div key="results" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-2" data-testid="scan-results">
                {(result.candidates || []).length === 0 ? (
                  <div className="flat-card p-10 text-center text-sm text-slate-500">{t("scan.empty_match", "No DB match. Try a clearer angle.")}</div>
                ) : (
                  result.candidates.map((c, idx) => (
                    <Link
                      key={c.id}
                      to={`/cards/${c.id}`}
                      data-testid={`scan-match-${c.id}`}
                      className="flat-card flex items-center gap-4 p-3 group"
                    >
                      <span className="text-xs font-bold text-slate-400 w-5">{(idx + 1).toString().padStart(2, "0")}</span>
                      <div className="w-12 h-16 bg-slate-100 rounded-md overflow-hidden flex-shrink-0">
                        {c.images?.small && <img src={c.images.small} alt={c.name} className="w-full h-full object-cover" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-900 truncate group-hover:text-[#E11D48]">{c.name}</div>
                        <div className="text-xs text-slate-500 truncate">{c.set?.name} · #{c.number}</div>
                        {c.rarity && <div className="text-[10px] text-[#2563EB] font-semibold mt-0.5">{c.rarity}</div>}
                      </div>
                      <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-[#E11D48]" />
                    </Link>
                  ))
                )}
                {typeof result.scans_remaining === "number" && (
                  <div className="text-xs text-slate-500 text-right pt-2 inline-flex items-center gap-1.5 justify-end w-full">
                    <Crown className="w-3 h-3 text-[#F59E0B]" />
                    {result.scans_remaining} free scans left
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
}
